import {BadgeActionArrowIcon, BadgeActionCompleteIcon, BadgeActionLockIcon} from '@/components/Icons';
import useExternalLinks from '@/hooks/useExternalLinks';
import type {BadgeAction} from '@/types/loyalty';
import {twx} from '@/utils/tailwindUtils';

type BadgeActionItemProps = {
    action: BadgeAction;
    isCurrent?: boolean;
};

// Bg / rounding live on the <li> so `first:`/`last:` match correctly within
// the parent <ul>. Padding + flex layout live on the inner element (div or
// button) and stretch to fill the row.
const rowClass = 'overflow-hidden first:rounded-t-xl last:rounded-b-xl';
const innerClass = 'flex w-full items-center justify-between gap-3 p-4 text-left';

type ContentProps = {
    action: BadgeAction;
    isCurrent: boolean;
};

const BadgeActionContent = ({action, isCurrent}: ContentProps) => {
    const {label, description, isCompleted} = action;
    const renderIcon = () => {
        if (isCompleted) return <BadgeActionCompleteIcon />;
        if (isCurrent) return <BadgeActionArrowIcon />;
        return <BadgeActionLockIcon />;
    };

    return (
        <>
            <div className='flex flex-col gap-px'>
                {label && <span className='text-16 text-cyan-accent leading-125'>{label}</span>}
                {description && <span className='text-16 text-white leading-15'>{description}</span>}
            </div>
            <div className='flex shrink-0 items-center text-cyan-accent'>{renderIcon()}</div>
        </>
    );
};

/**
 * Action row in the badge details popup.
 * - Completed action            → static block with the "complete" icon.
 * - Current pending action      → button that opens the external link via the
 *                                 parent app's external-link resolver.
 * - Future (non-current) action → static block with the "lock" icon (locked).
 */
const BadgeActionItem = ({action, isCurrent = false}: BadgeActionItemProps) => {
    const {url, isCompleted} = action;
    // Action URLs in the AEM payload are plain (non-SSO) URLs. Feed them in
    // as `ssoUrl` so the parent's `uwmExternalLinkService` runs them through
    // `iccService.fetchSSOLinks` and we get an SSO-wrapped URL back.
    const externalLink = url ? {ssoUrl: url} : null;
    const redirect = useExternalLinks({externalLink, params: {}});

    if (isCompleted || !isCurrent) {
        return (
            <li className={twx(rowClass, 'bg-charcoal', isCompleted && 'bg-cyan-accent/40')}>
                <div className={innerClass}>
                    <BadgeActionContent action={action} isCurrent={false} />
                </div>
            </li>
        );
    }

    return (
        <li className={twx(rowClass, 'bg-charcoal')}>
            <button
                data-testid='badge-action-item-button'
                type='button'
                onClick={() => redirect?.()}
                disabled={!redirect}
                className={twx(innerClass, 'cursor-pointer border-0 bg-transparent disabled:cursor-default')}
            >
                <BadgeActionContent action={action} isCurrent />
            </button>
        </li>
    );
};

export default BadgeActionItem;
